import { SAMPLE_WORKFLOW_RUN_REPORT_RAW } from '@/api/workflow-run-report'

// Local type definitions for mock data (API files no longer exist)
type Improvement = {
  id: string
  dateOfRecommendation: Date
  product: string
  productFamily: string
  businessUnit: string
  percentImprovement: string
  reportLink: string
  version: string
  status: string
  reportTitle: string
}

type ConfigSettings = {
  [key: string]: unknown
}

type WorkflowSession = {
  id: number
  workflow_type?: string
  trigger?: string
  status: string
  initiated_by?: string
  product_id?: number | null
  product_name?: string | null
  start_date?: string | null
  end_date?: string | null
  created_at: string
  modified_at?: string
  user_id?: number
  agent_id?: number
  agent_session_id?: string | null
  initial_prompt?: string | null
  agent_name?: string
  target?: string
  outcome?: string
  duration_seconds?: number | null
}

// Product Type - For UI/Form (not API)
type Product = {
  id: string
  name: string
  productFamily: string
  businessUnit: string
  node: string
  selected: boolean
}

// Helper to create dates in the past
const createDate = (daysAgo: number) => {
  const date = new Date()
  date.setDate(date.getDate() - daysAgo)
  return date
}

// Helper to create ISO date strings
const createISODate = (daysAgo: number) => {
  return createDate(daysAgo).toISOString()
}

/** Mutable store for mock report exports (POST + SSE + list GET). */
type MockReportExportRow = {
  id: string
  report_id: string
  type: string
  status: string
  progress: number
  file_url: string | null
  download_url: string | null
  error: string | null
  created_at: string
  updated_at: string
}

let mockReportExportSeq = 2
const mockReportExportStore: MockReportExportRow[] = (() => {
  const now = new Date().toISOString()
  return [
    {
      id: 'mock-export-1',
      report_id: 'sample-report-1',
      type: 'PDF',
      status: 'COMPLETED',
      progress: 100,
      file_url: '/api/v2/reports/exports/mock-export-1/download',
      download_url: '/api/v2/reports/exports/mock-export-1/download',
      error: null,
      created_at: now,
      updated_at: now,
    },
    {
      id: 'mock-export-2',
      report_id: 'sample-report-1',
      type: 'DOCX',
      status: 'COMPLETED',
      progress: 100,
      file_url: '/api/v2/reports/exports/mock-export-2/download',
      download_url: '/api/v2/reports/exports/mock-export-2/download',
      error: null,
      created_at: now,
      updated_at: now,
    },
  ]
})()

// Mock data for Improvements
export const mockImprovements: Improvement[] = [
  {
    id: '1',
    dateOfRecommendation: createDate(15),
    product: 'Product Alpha',
    productFamily: 'Processor Series X',
    businessUnit: 'Enterprise Computing',
    percentImprovement: '23%',
    reportLink: 'R-101',
    version: 'v1.0',
    status: 'Approved',
    reportTitle: 'Product Alpha Improvement Report',
  },
  {
    id: '2',
    dateOfRecommendation: createDate(13),
    product: 'Product Beta',
    productFamily: 'Memory Module Series Y',
    businessUnit: 'Data Center Solutions',
    percentImprovement: '45%',
    reportLink: 'R-102',
    version: 'v2.1',
    status: 'Unapproved',
    reportTitle: 'Product Beta Optimization Analysis',
  },
  {
    id: '3',
    dateOfRecommendation: createDate(11),
    product: 'Product Gamma',
    productFamily: 'Storage Controller Z',
    businessUnit: 'Enterprise Storage',
    percentImprovement: '12%',
    reportLink: 'R-103',
    version: 'v1.2',
    status: 'Rejected',
    reportTitle: 'Product Gamma Enhancement Proposal',
  },
  {
    id: '4',
    dateOfRecommendation: createDate(9),
    product: 'Product Delta',
    productFamily: 'Processor Series X',
    businessUnit: 'Enterprise Computing',
    percentImprovement: '38%',
    reportLink: 'R-104',
    version: 'v1.5',
    status: 'Approved',
    reportTitle: 'Product Delta Performance Improvement',
  },
  {
    id: '5',
    dateOfRecommendation: createDate(7),
    product: 'Product Epsilon',
    productFamily: 'Network Adapter Series W',
    businessUnit: 'Networking Solutions',
    percentImprovement: '19%',
    reportLink: 'R-105',
    version: 'v2.0',
    status: 'Unapproved',
    reportTitle: 'Product Epsilon Upgrade Recommendation',
  },
  {
    id: '6',
    dateOfRecommendation: createDate(5),
    product: 'Product Zeta',
    productFamily: 'Memory Module Series Y',
    businessUnit: 'Data Center Solutions',
    percentImprovement: '52%',
    reportLink: 'R-106',
    version: 'v1.3',
    status: 'Approved',
    reportTitle: 'Product Zeta Major Enhancement',
  },
  {
    id: '7',
    dateOfRecommendation: createDate(3),
    product: 'Product Alpha',
    productFamily: 'Processor Series X',
    businessUnit: 'Enterprise Computing',
    percentImprovement: '27%',
    reportLink: 'R-107',
    version: 'v1.8',
    status: 'Unapproved',
    reportTitle: 'Product Alpha Version 2.0 Recommendations',
  },
]

// Mock data for Products
export const mockProducts: Product[] = [
  {
    id: '1',
    name: 'Product Alpha',
    productFamily: 'Processor Series X',
    businessUnit: 'Enterprise Computing',
    node: '7nm',
    selected: true,
  },
  {
    id: '2',
    name: 'Product Beta',
    productFamily: 'Memory Module Series Y',
    businessUnit: 'Data Center Solutions',
    node: '5nm',
    selected: true,
  },
  {
    id: '3',
    name: 'Product Gamma',
    productFamily: 'Storage Controller Z',
    businessUnit: 'Enterprise Storage',
    node: '7nm',
    selected: false,
  },
  {
    id: '4',
    name: 'Product Delta',
    productFamily: 'Processor Series X',
    businessUnit: 'Enterprise Computing',
    node: '5nm',
    selected: true,
  },
  {
    id: '5',
    name: 'Product Epsilon',
    productFamily: 'Network Adapter Series W',
    businessUnit: 'Networking Solutions',
    node: '12nm',
    selected: false,
  },
  {
    id: '6',
    name: 'Product Zeta',
    productFamily: 'Memory Module Series Y',
    businessUnit: 'Data Center Solutions',
    node: '5nm',
    selected: true,
  },
  {
    id: '7',
    name: 'Product Eta',
    productFamily: 'GPU Compute Series V',
    businessUnit: 'High Performance Computing',
    node: '7nm',
    selected: false,
  },
  {
    id: '8',
    name: 'Product Theta',
    productFamily: 'AI Accelerator Series U',
    businessUnit: 'Artificial Intelligence',
    node: '5nm',
    selected: true,
  },
  {
    id: '9',
    name: 'Product Iota',
    productFamily: 'Encryption Module Series T',
    businessUnit: 'Security Solutions',
    node: '7nm',
    selected: false,
  },
  {
    id: '10',
    name: 'Product Kappa',
    productFamily: 'Processor Series X',
    businessUnit: 'Enterprise Computing',
    node: '5nm',
    selected: true,
  },
]

// Mock data for Configuration
export const mockConfigSettings: ConfigSettings = {
  id: '1',
  featuresEnabled: {
    improvements: true,
  },
  notifications: {
    sendOn: ['Email', 'Slack'],
    notificationsFor: ['Type 1', 'Type 2'],
    frequency: 2,
    frequencyUnit: 'Days',
  },
}

// Mock user data - V2 API spec
export const mockUser = {
  id: 'mock-user-sub',
  keycloak_sub: 'mock-user-sub',
  preferred_username: 'devuser',
  email: 'dev@example.com',
  roles: ['user', 'admin'],
  claims: {
    sub: 'mock-user-sub',
    email: 'dev@example.com',
  },
  organizations: [],
}

// Mock auth responses
const mockAuthMe = {
  message: 'User retrieved successfully',
  data: mockUser,
}

const mockAuthSession = {
  message: 'Session created successfully',
}

// Mock products (API format - V2 with nested organization, technology_node, foundry, business_unit)
const mkProduct = (id: number, name: string, bu: string, node: string, createdAt: string) => ({
  id: String(id),
  organization_id: null,
  external_id: null,
  name,
  data_availability_status: 'COMPLETE',
  is_activated: true,
  fpu_cost: 1,
  created_at: createdAt,
  organization: { id: 'org-1', name: 'ACME Semiconductor', external_id: 'ACME-001' },
  technology_node: { id: `node-${node}`, name: node, description: `${node} process` },
  foundry: { id: 'foundry-1', name: 'TSMC' },
  business_unit: { id: `bu-${bu}`, name: bu },
})

export const mockProductsApi = [
  mkProduct(1, 'BCM5820', 'Enterprise Computing', '7nm', createISODate(100)),
  mkProduct(2, 'BCM7445', 'Data Center Solutions', '5nm', createISODate(90)),
  mkProduct(3, 'BCM2711', 'Enterprise Storage', '7nm', createISODate(80)),
  mkProduct(4, 'BCM5821', 'Enterprise Computing', '5nm', createISODate(70)),
  mkProduct(5, 'BCM7446', 'Data Center Solutions', '5nm', createISODate(60)),
  mkProduct(6, 'BCM2712', 'Enterprise Storage', '7nm', createISODate(50)),
  mkProduct(7, 'BCM5822', 'Enterprise Computing', '7nm', createISODate(40)),
  mkProduct(8, 'BCM7447', 'Data Center Solutions', '5nm', createISODate(30)),
]

// Mock product configurations
const mockProductConfigurations = [
  {
    id: 1,
    product_id: 1,
    min_yield: 95,
    min_volume_lots: 5,
    min_volume_days: 7,
    created_at: createISODate(50),
    modified_at: createISODate(10),
  },
  {
    id: 2,
    product_id: 2,
    min_yield: 92,
    min_volume_lots: 3,
    min_volume_days: 5,
    created_at: createISODate(45),
    modified_at: createISODate(9),
  },
  {
    id: 3,
    product_id: 3,
    min_yield: 90,
    min_volume_lots: 4,
    min_volume_days: 6,
    created_at: createISODate(40),
    modified_at: createISODate(8),
  },
]

// Mock user products
export const mockUserProducts = [
  {
    id: 1,
    products_group_id: 1,
    name: 'BCM5820',
    business_unit: 'Enterprise Computing',
    node: '7nm',
    created_at: createISODate(100),
    modified_at: createISODate(10),
    data_source_count: 3,
    data_sources_url: '/api/v1/data-sources?product_id=1',
  },
  {
    id: 2,
    products_group_id: 2,
    name: 'BCM7445',
    business_unit: 'Data Center Solutions',
    node: '5nm',
    created_at: createISODate(90),
    modified_at: createISODate(9),
    data_source_count: 2,
    data_sources_url: '/api/v1/data-sources?product_id=2',
  },
  {
    id: 3,
    products_group_id: 3,
    name: 'BCM2711',
    business_unit: 'Enterprise Storage',
    node: '7nm',
    created_at: createISODate(80),
    modified_at: createISODate(8),
    data_source_count: 2,
    data_sources_url: '/api/v1/data-sources?product_id=3',
  },
]

// Mock data sources
export const mockDataSources = [
  {
    id: 1,
    name: 'WAT Data',
    path: '/data/wat/bcm5820',
    product_id: 1,
    product: {
      id: 1,
      name: 'BCM5820',
    },
    lots: ['LOT001', 'LOT002', 'LOT003'],
    created_at: createISODate(50),
  },
  {
    id: 2,
    name: 'Wafer Sort Data',
    path: '/data/wafer-sort/bcm5820',
    product_id: 1,
    product: {
      id: 1,
      name: 'BCM5820',
    },
    lots: ['LOT001', 'LOT002'],
    created_at: createISODate(45),
  },
  {
    id: 3,
    name: 'Final Test Data',
    path: '/data/final-test/bcm5820',
    product_id: 1,
    product: {
      id: 1,
      name: 'BCM5820',
    },
    lots: ['LOT001'],
    created_at: createISODate(40),
  },
  {
    id: 4,
    name: 'WAT Data',
    path: '/data/wat/bcm7445',
    product_id: 2,
    product: {
      id: 2,
      name: 'BCM7445',
    },
    lots: ['LOT004', 'LOT005'],
    created_at: createISODate(35),
  },
  {
    id: 5,
    name: 'Wafer Sort Data',
    path: '/data/wafer-sort/bcm7445',
    product_id: 2,
    product: {
      id: 2,
      name: 'BCM7445',
    },
    lots: ['LOT004'],
    created_at: createISODate(30),
  },
  {
    id: 6,
    name: 'WAT Data',
    path: '/data/wat/bcm2711',
    product_id: 3,
    product: {
      id: 3,
      name: 'BCM2711',
    },
    lots: ['LOT006', 'LOT007'],
    created_at: createISODate(25),
  },
  {
    id: 7,
    name: 'Wafer Sort Data',
    path: '/data/wafer-sort/bcm2711',
    product_id: 3,
    product: {
      id: 3,
      name: 'BCM2711',
    },
    lots: ['LOT006'],
    created_at: createISODate(20),
  },
]

/** Matches GET /api/v2/workflows/workflow-types (shape aligned with backend list_workflow_types). */
const MOCK_WORKFLOW_TYPES_V2: Array<{
  id: string
  slug: string
  prefect_deployment_name: string
  name: string
  description: string
}> = [
  {
    id: 'wt-def-yield-excursion',
    slug: 'yield-excursion',
    prefect_deployment_name: 'YIELD_EXCURSION',
    name: 'Yield Excursion Detection',
    description:
      'Automatically detects yield excursions and emerging negative trends. Identifies impacted lots and wafers, assigns severity, and determines whether issues are test-related or process-related.',
  },
  {
    id: 'wt-def-eval-harness',
    slug: 'eval-harness',
    prefect_deployment_name: 'EVAL_HARNESS',
    name: 'Evaluation Harness',
    description:
      'Runs the evaluation pipeline: data loading, evaluation, determinism, and scoring.',
  },
  {
    id: 'wt-def-feedback-learning',
    slug: 'feedback-learning',
    prefect_deployment_name: 'FEEDBACK_LEARNING',
    name: 'Feedback Learning',
    description:
      'Processes pending feedback into policy updates. Queries PENDING feedback, groups by product and scope, generates updated policies via LLM, and marks feedback as APPLIED.',
  },
  {
    id: 'wt-def-ingestion-optimal-plus',
    slug: 'ingestion-optimal-plus',
    prefect_deployment_name: 'INGESTION_OPTIMAL_PLUS',
    name: 'Optimal+ Data Ingestion',
    description:
      'Ingests semiconductor test data from Optimal+ into the Emergence data platform. Submits extraction jobs, polls for completion, and loads results into the Iceberg warehouse.',
  },
]

// Mock workflow sessions
export const mockWorkflowSessions: WorkflowSession[] = [
  {
    id: 1,
    user_id: 1,
    agent_id: 1,
    status: 'completed' as const,
    initial_prompt: 'Analyze yield for BCM5820',
    agent_session_id: 'session-1',
    start_date: '2026-01-12T22:30:00.000Z',
    end_date: '2026-01-12T22:42:34.000Z',
    created_at: '2026-01-12T22:30:00.000Z',
    modified_at: '2026-01-12T22:42:34.000Z',
    product_id: 1,
    product_name: 'Aria Pro X1',
    workflow_type: 'YIELD_EXCURSION',
    agent_name: 'Yield Excursions',
    trigger: 'Manual',
    initiated_by: 'John Doe',
    target: 'Aria Pro X1',
    outcome: '3 yield excursions detected',
    duration_seconds: 12 * 60 + 34,
  },
  {
    id: 2,
    user_id: 1,
    agent_id: 1,
    status: 'failed' as const,
    initial_prompt: 'Analyze yield for Aria Pro X1',
    agent_session_id: 'session-2',
    start_date: '2026-01-12T22:30:00.000Z',
    end_date: '2026-01-12T22:42:34.000Z',
    created_at: '2026-01-12T22:30:00.000Z',
    modified_at: '2026-01-12T22:42:34.000Z',
    product_id: 2,
    product_name: 'Aria Pro X1',
    workflow_type: 'YIELD_EXCURSION',
    agent_name: 'Yield Excursions',
    trigger: 'Excursion',
    initiated_by: 'System',
    target: 'Aria Pro X1',
    outcome: '3 yield excursions detected',
    duration_seconds: 12 * 60 + 34,
  },
  {
    id: 3,
    user_id: 1,
    agent_id: 1,
    status: 'completed' as const,
    initial_prompt: 'Analyze yield for Aria Pro X1',
    agent_session_id: 'session-3',
    start_date: '2026-01-12T22:30:00.000Z',
    end_date: '2026-01-12T22:42:34.000Z',
    created_at: '2026-01-12T22:30:00.000Z',
    modified_at: '2026-01-12T22:42:34.000Z',
    product_id: 3,
    product_name: 'Aria Pro X1',
    workflow_type: 'YIELD_EXCURSION',
    agent_name: 'Yield Excursions',
    trigger: 'Scheduled',
    initiated_by: 'Scheduler',
    target: 'Aria Pro X1',
    outcome: '3 yield excursions detected',
    duration_seconds: 12 * 60 + 34,
  },
  {
    id: 4,
    user_id: 1,
    agent_id: 1,
    status: 'completed' as const,
    initial_prompt: 'Analyze yield for Aria Pro X1',
    agent_session_id: 'session-4',
    start_date: '2026-01-12T22:30:00.000Z',
    end_date: '2026-01-12T22:42:34.000Z',
    created_at: '2026-01-12T22:30:00.000Z',
    modified_at: '2026-01-12T22:42:34.000Z',
    product_id: 1,
    product_name: 'Aria Pro X1',
    workflow_type: 'YIELD_EXCURSION',
    agent_name: 'Yield Excursions',
    trigger: 'Manual',
    initiated_by: 'John Doe',
    target: 'Aria Pro X1',
    outcome: '3 yield excursions detected',
    duration_seconds: 12 * 60 + 34,
  },
  {
    id: 5,
    user_id: 1,
    agent_id: 1,
    status: 'running' as const,
    initial_prompt: 'Analyze yield for Aria Pro X1',
    agent_session_id: 'session-5',
    start_date: '2026-01-12T22:30:00.000Z',
    end_date: null,
    created_at: '2026-01-12T22:30:00.000Z',
    modified_at: '2026-01-12T22:42:34.000Z',
    product_id: 2,
    product_name: 'Aria Pro X1',
    workflow_type: 'YIELD_EXCURSION',
    agent_name: 'Yield Excursions',
    trigger: 'Excursion',
    initiated_by: 'System',
    target: 'Aria Pro X1',
    outcome: '3 yield excursions detected',
    duration_seconds: 12 * 60 + 34,
  },
  {
    id: 6,
    user_id: 1,
    agent_id: 1,
    status: 'completed',
    initial_prompt: 'Identify yield process insights',
    agent_session_id: 'session-6',
    start_date: '2026-01-12T22:30:00.000Z',
    end_date: '2026-01-12T22:43:00.000Z',
    created_at: '2026-01-12T22:30:00.000Z',
    modified_at: '2026-01-12T22:43:00.000Z',
    product_id: 2,
    product_name: 'Aria Pro X1',
    workflow_type: 'YIELD_PROCESS_INSIGHTS',
    agent_name: 'Yield Process Insights',
    trigger: 'Manual',
    initiated_by: 'John Doe',
    target: 'Aria Pro X1',
    outcome: '3 top opportunities identified',
    duration_seconds: 13 * 60,
  },
  {
    id: 7,
    user_id: 1,
    agent_id: 1,
    status: 'completed',
    initial_prompt: 'Find product group outliers',
    agent_session_id: 'session-7',
    start_date: '2026-01-12T22:30:00.000Z',
    end_date: '2026-01-12T22:44:00.000Z',
    created_at: '2026-01-12T22:30:00.000Z',
    modified_at: '2026-01-12T22:44:00.000Z',
    product_id: 2,
    product_name: 'Aria Pro X1',
    workflow_type: 'CROSS_PRODUCT_ANALYSIS',
    agent_name: 'Product Group Outliers',
    trigger: 'Scheduled',
    initiated_by: 'Scheduler',
    target: 'Aria Pro X1',
    outcome: '2 outlier groups found',
    duration_seconds: 14 * 60,
  },
]

// Mock output files
const mockOutputFiles = (sessionId: number) => [
  {
    id: 'file-1',
    name: 'analysis_report_jsonpaths.md',
    path: `/workflows/${sessionId}/analysis_report_jsonpaths.md`,
    uri: `file:///workflows/${sessionId}/analysis_report_jsonpaths.md`,
    url: `/api/v1/workflow-sessions/${sessionId}/output-files/download?path=analysis_report_jsonpaths.md`,
    mime_type: 'text/markdown',
    size: 10240,
    extension: 'md',
    created_at: createISODate(1),
  },
  {
    id: 'file-2',
    name: 'yield_data.json',
    path: `/workflows/${sessionId}/yield_data.json`,
    uri: `file:///workflows/${sessionId}/yield_data.json`,
    url: `/api/v1/workflow-sessions/${sessionId}/output-files/download?path=yield_data.json`,
    mime_type: 'application/json',
    size: 20480,
    extension: 'json',
    created_at: createISODate(1),
  },
  {
    id: 'file-3',
    name: 'chart_yield_trend.json',
    path: `/workflows/${sessionId}/charts/chart_yield_trend.json`,
    uri: `file:///workflows/${sessionId}/charts/chart_yield_trend.json`,
    url: `/api/v1/workflow-sessions/${sessionId}/output-files/download?path=charts/chart_yield_trend.json`,
    mime_type: 'application/json',
    size: 15360,
    extension: 'json',
    created_at: createISODate(1),
  },
]

// Mock chart output files - must match PlotlyChart structure
const mockChartOutputFiles = [
  {
    data: [
      {
        type: 'scatter',
        mode: 'lines+markers',
        name: 'Yield Trend',
        x: [1, 2, 3, 4, 5],
        y: [95, 94, 93, 92, 91],
      },
    ],
    layout: {
      title: 'Yield Trend Over Time',
      xaxis: { title: 'Time' },
      yaxis: { title: 'Yield (%)' },
    },
  },
  {
    data: [
      {
        type: 'histogram',
        name: 'Yield Distribution',
        x: [85, 85, 90, 90, 90, 90, 90, 95, 95, 95, 95, 95, 95, 95, 100, 100, 100, 100, 100],
      },
    ],
    layout: {
      title: 'Yield Distribution',
      xaxis: { title: 'Yield (%)' },
      yaxis: { title: 'Frequency' },
    },
  },
]

// Mock data mapping - maps API paths to mock responses
// This is only used in dev mode
export const mockDataMap: Record<string, any> = {
  // Auth endpoints
  '/api/v1/auth/me': mockAuthMe,
  '/api/v1/auth/session': mockAuthSession,

  // Products
  '/api/v1/products': { message: 'Products retrieved successfully', data: mockProductsApi },

  // Product configurations
  '/api/v1/product-configurations': {
    message: 'Product configurations retrieved successfully',
    data: mockProductConfigurations,
  },

  // User products
  '/api/v1/users/me/products': {
    message: 'User products retrieved successfully',
    data: mockUserProducts,
  },

  // Data sources
  '/api/v1/data-sources': { message: 'Data sources retrieved successfully', data: mockDataSources },

  // Workflow sessions
  '/api/v1/workflow-sessions': {
    message: 'Workflow sessions retrieved successfully',
    data: mockWorkflowSessions,
  },

  // Config
  '/api/config': { message: 'Config retrieved successfully', data: mockConfigSettings },

  // Improvements
  '/api/improvements': { message: 'Improvements retrieved successfully', data: mockImprovements },
}

// Helper to get mock data for a path
// Handles dynamic paths like /api/v1/workflow-sessions/:id
export function getMockData(path: string, method: string = 'GET', body?: unknown): any {
  // Normalize path - remove query params and ensure leading slash
  let normalizedPath = path.split('?')[0]
  try {
    const urlObj = new URL(path)
    normalizedPath = urlObj.pathname + urlObj.search
  } catch (err) {
    console.error('Normalize URL', err)
  }
  if (!normalizedPath.startsWith('/')) {
    normalizedPath = '/' + normalizedPath
  }

  // V2 auth: login, register, forgot-password, session
  if (normalizedPath === '/api/v2/auth/login' && method === 'POST') {
    return { message: 'successful' }
  }
  if (normalizedPath === '/api/v2/auth/register' && method === 'POST') {
    return { message: 'successful' }
  }
  if (normalizedPath === '/api/v2/auth/forgot-password' && method === 'POST') {
    return { message: 'successful' }
  }
  if (normalizedPath === '/api/v2/auth/session' && method === 'POST') {
    return { message: 'successful', data: { id: mockUser.id, keycloak_sub: mockUser.keycloak_sub } }
  }
  if (normalizedPath === '/api/v2/auth/me' && method === 'GET') {
    return { message: 'User retrieved successfully', data: mockUser }
  }

  // Handle dynamic workflow session paths
  const workflowSessionMatch = normalizedPath.match(/^\/api\/v1\/workflow-sessions\/(\d+)$/)
  if (workflowSessionMatch && method === 'GET') {
    const sessionId = parseInt(workflowSessionMatch[1], 10)
    const session = mockWorkflowSessions.find((s) => s.id === sessionId)
    if (session) {
      return { message: 'Workflow session retrieved successfully', data: session }
    }
  }

  // Handle workflow session start
  const startMatch = path.match(/^\/api\/v1\/workflow-sessions\/(\d+)\/start$/)
  if (startMatch && method === 'POST') {
    const sessionId = parseInt(startMatch[1], 10)
    const session = mockWorkflowSessions.find((s) => s.id === sessionId)
    if (session) {
      // Return updated session with running status
      return { message: 'Workflow session started', data: { ...session, status: 'running' } }
    }
  }

  // Handle workflow session stop
  const stopMatch = path.match(/^\/api\/v1\/workflow-sessions\/(\d+)\/stop$/)
  if (stopMatch && method === 'POST') {
    const sessionId = parseInt(stopMatch[1], 10)
    const session = mockWorkflowSessions.find((s) => s.id === sessionId)
    if (session) {
      // Return updated session with stopped status
      return { message: 'Workflow session stopped', data: { ...session, status: 'stopped' } }
    }
  }

  // Handle workflow session output files
  const outputFilesMatch = path.match(
    /^\/api\/v1\/workflow-sessions\/(\d+)\/output-files(?:\?.*)?$/,
  )
  if (outputFilesMatch && method === 'GET') {
    const sessionId = parseInt(outputFilesMatch[1], 10)
    return { message: 'Output files retrieved successfully', data: mockOutputFiles(sessionId) }
  }

  // Handle workflow session output files download
  const downloadMatch = path.match(
    /^\/api\/v1\/workflow-sessions\/(\d+)\/output-files\/download(?:\?.*)?$/,
  )
  if (downloadMatch && method === 'GET') {
    // Return a mock markdown file
    const mockMarkdown =
      '# Analysis Report\n\nThis is a mock analysis report generated in dev mode.\n\n## Summary\n\n- Yield analysis completed\n- No issues detected\n'
    return new Blob([mockMarkdown], { type: 'text/markdown' })
  }

  // Handle workflow session charts
  const chartsMatch = path.match(
    /^\/api\/v1\/workflow-sessions\/(\d+)\/output-files\/charts(?:\?.*)?$/,
  )
  if (chartsMatch && method === 'GET') {
    return { message: 'Chart files retrieved successfully', data: mockChartOutputFiles }
  }

  // Handle workflow session stream (SSE)
  const streamMatch = path.match(/^\/api\/v1\/workflow-sessions\/(\d+)\/stream$/)
  if (streamMatch && method === 'POST') {
    // Return a mock SSE stream
    const sessionId = parseInt(streamMatch[1], 10)
    const mockStream = new ReadableStream({
      start(controller) {
        // Send initial event
        controller.enqueue(
          new TextEncoder().encode(
            'data: {"event":"stream","data":{"functions":[{"id":"func-1","name":"analyze_yield","progress":0,"current_message":"Starting analysis...","status":"running"}]}}\n\n',
          ),
        )

        // Simulate progress
        let progress = 0
        const interval = setInterval(() => {
          progress += 25
          if (progress <= 100) {
            controller.enqueue(
              new TextEncoder().encode(
                `data: {"event":"stream","data":{"functions":[{"id":"func-1","name":"analyze_yield","progress":${progress},"current_message":"Processing... ${progress}%","status":"running"}]}}\n\n`,
              ),
            )
          } else {
            controller.enqueue(
              new TextEncoder().encode(
                'data: {"event":"stream-stopped","data":{"message":"Analysis complete"}}\n\n',
              ),
            )
            clearInterval(interval)
            controller.close()
          }
        }, 500)
      },
    })
    return new Response(mockStream, {
      headers: { 'Content-Type': 'text/event-stream' },
    })
  }

  // Handle product configuration update
  const productConfigMatch = path.match(/^\/api\/v1\/products\/(\d+)\/configuration$/)
  if (productConfigMatch && method === 'PUT' && body) {
    const productId = parseInt(productConfigMatch[1], 10)
    const config = mockProductConfigurations.find((c) => c.product_id === productId)
    if (config) {
      return {
        message: 'Configuration updated successfully',
        data: { ...config, ...(body as Record<string, unknown>) },
      }
    }
  }

  // Handle create workflow session
  if (normalizedPath === '/api/v1/workflow-sessions' && method === 'POST' && body) {
    const newSession: WorkflowSession = {
      id: mockWorkflowSessions.length + 1,
      user_id: 1,
      agent_id: (body as { agent_id?: number }).agent_id || 1,
      status: 'created' as const,
      initial_prompt: (body as { initial_prompt?: string }).initial_prompt || null,
      agent_session_id: null,
      start_date: (body as { start_date?: string }).start_date || null,
      end_date: (body as { end_date?: string }).end_date || null,
      created_at: new Date().toISOString(),
      modified_at: new Date().toISOString(),
      product_id: (body as { product_id?: number }).product_id || null,
      product_name: null,
      workflow_type: 'YIELD_EXCURSION',
      agent_name: 'Yield Excursions',
      trigger: 'Manual',
      initiated_by: 'John Doe',
      target: 'Aria Pro X1',
      outcome: '—',
      duration_seconds: null,
    }
    mockWorkflowSessions.push(newSession)
    return { message: 'Workflow session created successfully', data: newSession }
  }

  // ===== V2 Workflow Endpoints =====

  // Helper to convert mock workflow session to WorkflowRunListItem
  const convertToWorkflowRunListItem = (session: WorkflowSession) => {
    const duration =
      session.duration_seconds ||
      (session.start_date && session.end_date
        ? Math.floor(
            (new Date(session.end_date).getTime() - new Date(session.start_date).getTime()) / 1000,
          )
        : null)

    return {
      id: String(session.id),
      run_number: session.id,
      workflow_type: session.workflow_type || 'YIELD_EXCURSION',
      workflow_version_label: session.agent_name?.trim()
        ? session.agent_name
        : session.workflow_type || 'Yield Excursion',
      trigger_type: session.trigger || 'Manual',
      status: session.status,
      progress_percent:
        session.status === 'running' ? 50 : session.status === 'completed' ? 100 : null,
      created_by: session.initiated_by
        ? {
            id: '1',
            username: session.initiated_by.toLowerCase().replace(/\s+/g, '.'),
            firstName: session.initiated_by.split(/\s+/)[0] || 'User',
            lastName: session.initiated_by.split(/\s+/).slice(1).join(' ') || '',
            email: `${session.initiated_by.toLowerCase().replace(/\s+/g, '.')}@example.com`,
          }
        : null,
      inputs: session.product_id
        ? {
            product: { id: String(session.product_id), name: session.product_name || 'Unknown' },
            lot_start_date: session.start_date || new Date().toISOString(),
            lot_end_date: session.end_date || new Date().toISOString(),
          }
        : null,
      created_at: session.created_at,
      excursion_events: [],
      started_at: session.start_date || null,
      completed_at: session.end_date || null,
    }
  }

  // Handle GET /api/v2/policies?product_id= — policy list for run info
  if (
    (normalizedPath === '/api/v2/policies' || normalizedPath.startsWith('/api/v2/policies?')) &&
    method === 'GET'
  ) {
    return {
      message: 'success',
      data: [
        {
          id: 'mock-policy-rca',
          source: 'product',
          product: { id: '1', name: 'Mock Product' },
          organization_id: 'mock-org',
          scope: 'RCA',
          is_active: true,
          current_version: 1,
          version: {
            id: 'mock-ver-1',
            version_number: 1,
            rules: null,
            diff: null,
            change_description: null,
            rationale: null,
            decision_source: null,
            created_at: new Date().toISOString(),
          },
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
        {
          id: 'mock-policy-excursion',
          source: 'product',
          product: { id: '1', name: 'Mock Product' },
          organization_id: 'mock-org',
          scope: 'EXCURSION_DETECTION',
          is_active: true,
          current_version: 1,
          version: {
            id: 'mock-ver-2',
            version_number: 1,
            rules: null,
            diff: null,
            change_description: null,
            rationale: null,
            decision_source: null,
            created_at: new Date().toISOString(),
          },
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
      ],
    }
  }

  // Handle GET /api/v2/workflows/workflow-runs - list workflow runs
  const workflowRunsBase = '/api/v2/workflows/workflow-runs'
  if (
    (normalizedPath === workflowRunsBase || normalizedPath.startsWith(workflowRunsBase + '?')) &&
    method === 'GET'
  ) {
    let items = mockWorkflowSessions.map(convertToWorkflowRunListItem)
    const queryIndex = path.indexOf('?')
    if (queryIndex >= 0) {
      const searchParams = new URLSearchParams(path.slice(queryIndex + 1))
      const flowTypes = searchParams.getAll('flow_type')
      if (flowTypes.length > 0) {
        const allowed = new Set(flowTypes.map((t) => t.toUpperCase()))
        items = items.filter((item) => allowed.has((item.workflow_type ?? '').toUpperCase()))
      }
      const sortBy = searchParams.get('sort_by')
      const sortOrder = searchParams.get('sort_order')
      if (sortBy && sortOrder) {
        const colMap: Record<string, keyof (typeof items)[0]> = {
          id: 'id',
          flow_type: 'workflow_type',
          status_message: 'status',
          created_at: 'created_at',
          trigger_type: 'trigger_type',
        }
        const col = colMap[sortBy] ?? sortBy
        const asc = sortOrder === 'asc'
        items = [...items].sort((a, b) => {
          const aVal = a[col] ?? ''
          const bVal = b[col] ?? ''
          const cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true })
          return asc ? cmp : -cmp
        })
      }
    }
    return {
      message: 'Workflow runs retrieved successfully',
      data: {
        items,
        pagination: {
          page: 1,
          page_size: 20,
          total_items: items.length,
          total_pages: 1,
          has_next: false,
          has_previous: false,
        },
      },
    }
  }

  // Handle GET /api/v2/workflows/workflow-runs/{run_id}/logs - logs snapshot
  const workflowRunLogsMatch = normalizedPath.match(
    /^\/api\/v2\/workflows\/workflow-runs\/([^/]+)\/logs$/,
  )
  if (workflowRunLogsMatch && method === 'GET') {
    const runId = workflowRunLogsMatch[1]
    return {
      message: 'Logs retrieved successfully',
      data: {
        workflow_id: runId,
        logs: [
          { level: 'INFO', message: 'Workflow started', timestamp: new Date().toISOString() },
          {
            level: 'INFO',
            message: 'Analyzing yield data...',
            timestamp: new Date().toISOString(),
          },
          { level: 'INFO', message: 'Analysis complete', timestamp: new Date().toISOString() },
        ],
        first_log_entry_id: null,
        last_log_entry_id: null,
      },
    }
  }

  // Handle GET /api/v2/workflows/workflow-runs/{run_id}/log-updates - SSE stream
  const workflowRunLogUpdatesMatch = normalizedPath.match(
    /^\/api\/v2\/workflows\/workflow-runs\/([^/]+)\/log-updates$/,
  )
  if (workflowRunLogUpdatesMatch && method === 'GET') {
    const runId = workflowRunLogUpdatesMatch[1]
    const mockLogsStream = new ReadableStream({
      start(controller) {
        const encoder = new TextEncoder()
        const sendEvent = (data: Record<string, unknown>) => {
          controller.enqueue(encoder.encode(`data: ${JSON.stringify(data)}\n\n`))
        }
        sendEvent({
          workflow_id: runId,
          logs: [
            { level: 'INFO', message: 'Streaming logs...', timestamp: new Date().toISOString() },
          ],
          first_log_entry_id: null,
          last_log_entry_id: null,
        })
        const t = setTimeout(() => {
          sendEvent({
            workflow_id: runId,
            logs: [
              { level: 'INFO', message: 'Streaming logs...', timestamp: new Date().toISOString() },
              {
                level: 'INFO',
                message: 'Mock log update received',
                timestamp: new Date().toISOString(),
              },
            ],
            first_log_entry_id: null,
            last_log_entry_id: null,
          })
          controller.close()
        }, 1000)
        return () => clearTimeout(t)
      },
    })
    return new Response(mockLogsStream, {
      headers: { 'Content-Type': 'text/event-stream' },
    })
  }

  // Handle GET /api/v2/workflows/workflow-runs/{run_id}/report - get workflow run report
  const workflowRunReportMatch = normalizedPath.match(
    /^\/api\/v2\/workflows\/workflow-runs\/([^/]+)\/report$/,
  )
  if (workflowRunReportMatch && method === 'GET') {
    const runId = workflowRunReportMatch[1]
    const session = mockWorkflowSessions.find((s) => String(s.id) === runId)
    if (session && session.status === 'completed') {
      const sections =
        (SAMPLE_WORKFLOW_RUN_REPORT_RAW.sections as Array<{
          id: string
          title: string
          content: string
        }>) ?? []
      return {
        report: {
          id: 'sample-report-1',
          workflow: {
            id: runId,
            type: session.workflow_type ?? null,
            trigger_type: session.trigger ?? null,
            user_status: session.status,
            status_message: 'Workflow completed successfully',
            created_at: session.created_at,
          },
          version: 1,
          type: 'yield_excursion',
          title: SAMPLE_WORKFLOW_RUN_REPORT_RAW.title ?? 'Report',
          severity: null,
          is_published: false,
          created_at: session.created_at,
          updated_at: session.created_at,
          author: { type: 'user', id: '1' },
        },
        table_of_contents: {
          enabled: true,
          title: 'Table of Contents',
          depth: 3,
          items: sections.map((s, i) => ({
            id: s.id,
            title: s.title,
            level: 2,
            order: i,
            anchor: s.id,
            children: [],
          })),
        },
        sections: sections.map((s, i) => ({
          id: s.id,
          order: i,
          heading_level: 2,
          title: s.title,
          content: s.content,
          charts: [],
          attachments: [],
        })),
      }
    }
    return new Response(JSON.stringify({ detail: 'Report not found' }), {
      status: 404,
      headers: { 'Content-Type': 'application/json' },
    })
  }

  // Handle GET /api/v2/reports/exports/stream — SSE (active exports snapshot + updates)
  if (normalizedPath === '/api/v2/reports/exports/stream' && method === 'GET') {
    const mockExportsStream = new ReadableStream({
      start(controller) {
        const encoder = new TextEncoder()
        const active = mockReportExportStore.filter(
          (r) => r.status === 'PENDING' || r.status === 'PROCESSING',
        )
        controller.enqueue(
          encoder.encode(
            `event: report-export\ndata: ${JSON.stringify(active)}\n\n`,
          ),
        )
        const t = setTimeout(() => {
          for (const row of mockReportExportStore) {
            if (row.status === 'PENDING' || row.status === 'PROCESSING') {
              row.status = 'COMPLETED'
              row.progress = 100
              row.download_url = `/api/v2/reports/exports/${row.id}/download`
              row.file_url = row.download_url
              row.updated_at = new Date().toISOString()
              controller.enqueue(
                encoder.encode(
                  `event: report-export\ndata: ${JSON.stringify(row)}\n\n`,
                ),
              )
            }
          }
          controller.close()
        }, 400)
        return () => clearTimeout(t)
      },
    })
    return new Response(mockExportsStream, {
      headers: { 'Content-Type': 'text/event-stream' },
    })
  }

  // Handle POST /api/v2/reports/exports — create async export jobs
  if (normalizedPath === '/api/v2/reports/exports' && method === 'POST') {
    const payload = body && typeof body === 'object' && !Array.isArray(body) ? body : {}
    const reportId =
      typeof (payload as { report_id?: string }).report_id === 'string'
        ? (payload as { report_id: string }).report_id
        : 'sample-report-1'
    const rawTypes = (payload as { types?: string[] }).types
    const types = Array.isArray(rawTypes) && rawTypes.length > 0 ? rawTypes : ['PDF']
    const now = new Date().toISOString()
    const created: MockReportExportRow[] = []
    for (const t of types) {
      const id = `mock-export-${++mockReportExportSeq}`
      const row: MockReportExportRow = {
        id,
        report_id: reportId,
        type: String(t).toUpperCase(),
        status: 'PENDING',
        progress: 0,
        file_url: null,
        download_url: null,
        error: null,
        created_at: now,
        updated_at: now,
      }
      mockReportExportStore.push(row)
      created.push(row)
    }
    return new Response(JSON.stringify(created), {
      status: 202,
      headers: { 'Content-Type': 'application/json' },
    })
  }

  // Handle GET /api/v2/reports/exports/{export_id}/download — mock file
  const reportExportDownloadMatch = normalizedPath.match(
    /^\/api\/v2\/reports\/exports\/([^/]+)\/download$/,
  )
  if (reportExportDownloadMatch && method === 'GET') {
    const blob = new Blob(['Mock export file\n'], { type: 'application/pdf' })
    return new Response(blob, {
      status: 200,
      headers: {
        'Content-Type': 'application/pdf',
        'Content-Disposition': 'attachment; filename="mock-export.pdf"',
      },
    })
  }

  // Handle GET /api/v2/reports/exports — list async report exports for the current user
  if (method === 'GET' && /^\/api\/v2\/reports\/exports(\?.*)?$/.test(normalizedPath)) {
    return mockReportExportStore.map((r) => ({ ...r }))
  }

  // Handle GET /api/v2/reports/{report_id}/comments — list feedback for a report
  const reportCommentsMatch = normalizedPath.match(/^\/api\/v2\/reports\/([^/]+)\/comments$/)
  if (reportCommentsMatch && method === 'GET') {
    const reportId = reportCommentsMatch[1]
    const now = new Date().toISOString()
    return {
      report: {
        id: reportId,
        title: 'Mock report',
        type: 'yield_excursion',
        severity: null,
        is_published: false,
        created_at: now,
      },
      items: [
        {
          id: 'mock-feedback-1',
          report_id: reportId,
          section_id: 'section-intro',
          chart_id: null,
          attachment_id: null,
          selection_start: 0,
          selection_end: 42,
          content:
            'This is sample feedback in mock backend mode. All fields appear in the Comments tab.',
          author: { id: 'mock-user', name: 'Mock User' },
          created_at: now,
          feedback_scope: 'section',
          feedback_class: 'actionable',
          feedback_status: 'pending',
        },
      ],
      total: 1,
    }
  }

  // Handle GET /api/v2/workflows/workflow-runs/{run_id} - get workflow run details
  const workflowRunDetailMatch = normalizedPath.match(
    /^\/api\/v2\/workflows\/workflow-runs\/([^/]+)$/,
  )
  if (workflowRunDetailMatch && method === 'GET') {
    const runId = workflowRunDetailMatch[1]
    const session = mockWorkflowSessions.find((s) => String(s.id) === runId)
    if (session) {
      const detail = {
        id: String(session.id),
        workflow_type: session.workflow_type || 'YIELD_EXCURSION',
        workflow_version_label: session.agent_name?.trim()
          ? session.agent_name
          : session.workflow_type || 'Yield Excursion',
        workflow_version_name: session.agent_name?.trim() || 'Yield excursion (mock)',
        trigger_type: session.trigger || 'Manual',
        status: session.status,
        status_message: session.status === 'completed' ? 'Workflow completed successfully' : null,
        progress_percent:
          session.status === 'running' ? 50 : session.status === 'completed' ? 100 : null,
        error_message: session.status === 'failed' ? 'Workflow execution failed' : null,
        created_by: session.initiated_by
          ? {
              id: '1',
              username: session.initiated_by.toLowerCase().replace(/\s+/g, '.'),
              firstName: session.initiated_by.split(/\s+/)[0] || 'User',
              lastName: session.initiated_by.split(/\s+/).slice(1).join(' ') || '',
              email: `${session.initiated_by.toLowerCase().replace(/\s+/g, '.')}@example.com`,
            }
          : null,
        inputs: session.product_id
          ? {
              products: [
                { id: String(session.product_id), name: session.product_name || 'Unknown' },
              ],
              lot_start_date: session.start_date || new Date().toISOString(),
              lot_end_date: session.end_date || new Date().toISOString(),
            }
          : null,
        outputs:
          session.status === 'completed'
            ? {
                excursions_detected: 3,
                reports: 1,
              }
            : null,
        excursion_events:
          session.status === 'completed'
            ? [
                {
                  id: 'exc-1',
                  excursion_number: 1,
                  yield_percent: 87.5,
                  wafers_impacted: 254,
                  lots_impacted: 25,
                  severity: 'HIGH',
                  summary:
                    'Analysis shows a downward yield trend of more than 12% in the selected timeframe.',
                  excursion_status: 'NEW',
                  assignee: null,
                  data_source: 'YIELD',
                },
              ]
            : [],
        reports:
          session.status === 'completed'
            ? [
                {
                  id: 'sample-report-1',
                  title: SAMPLE_WORKFLOW_RUN_REPORT_RAW.title ?? 'Sample Report',
                  version: 1,
                  created_at: session.created_at,
                  pdf_link: `/api/v2/reports/sample-report-1/download?type=PDF`,
                  word_link: `/api/v2/reports/sample-report-1/download?type=DOCX`,
                },
              ]
            : [],
        created_at: session.created_at,
        started_at: session.start_date || null,
        completed_at: session.end_date || null,
      }
      return { message: 'Workflow run retrieved successfully', data: detail }
    }
  }

  // Handle POST /api/v2/workflows/workflow-runs - create workflow run
  if (normalizedPath === '/api/v2/workflows/workflow-runs' && method === 'POST' && body) {
    const bodyData = body as { workflow_type?: string; parameters?: Record<string, unknown> }
    const runId = `mock-run-${mockWorkflowSessions.length + 1}-${Date.now()}`
    const newRun = {
      id: runId,
      prefect_run_id: `prefect-${runId}`,
      workflow_type: bodyData.workflow_type || 'YIELD_EXCURSION',
      trigger_type: 'MANUAL',
      status: 'PENDING',
      status_message: 'Workflow queued for execution',
      progress_percent: 0,
      created_by: {
        id: '1',
        username: 'user@example.com',
        firstName: 'John',
        lastName: 'Doe',
        email: 'user@example.com',
      },
      inputs: bodyData.parameters?.product_id
        ? {
            product: {
              id: String(bodyData.parameters.product_id),
              name: (bodyData.parameters.product_name as string) || 'Unknown',
            },
            lot_start_date:
              (bodyData.parameters.lot_start_date as string) || new Date().toISOString(),
            lot_end_date: (bodyData.parameters.lot_end_date as string) || new Date().toISOString(),
          }
        : null,
      created_at: new Date().toISOString(),
    }
    return { message: 'successful', data: newRun }
  }

  // Handle POST /api/v2/workflows/workflow-runs/{run_id}/cancel - cancel workflow run
  const cancelRunMatch = normalizedPath.match(/^\/api\/v2\/workflows\/workflow-runs\/(.+)\/cancel$/)
  if (cancelRunMatch && method === 'POST') {
    const runId = cancelRunMatch[1]
    return {
      message: 'Workflow run cancelled successfully',
      data: {
        id: runId,
        status: 'CANCELLED',
        status_message: 'Workflow run was cancelled',
        completed_at: new Date().toISOString(),
      },
    }
  }

  // Handle POST /api/v2/workflows/workflow-runs/{run_id}/retry - retry workflow run
  const retryRunMatch = normalizedPath.match(/^\/api\/v2\/workflows\/workflow-runs\/(.+)\/retry$/)
  if (retryRunMatch && method === 'POST') {
    const runId = retryRunMatch[1]
    const newRun = {
      id: String(mockWorkflowSessions.length + 1),
      workflow_type: 'YIELD_EXCURSION',
      trigger_type: 'Manual',
      status: 'created',
      status_message: null,
      progress_percent: null,
      created_by: {
        id: '1',
        username: 'user@example.com',
        firstName: 'John',
        lastName: 'Doe',
        email: 'user@example.com',
      },
      inputs: {
        product: { id: '1', name: 'Unknown' },
        lot_start_date: new Date().toISOString(),
        lot_end_date: new Date().toISOString(),
      },
      created_at: new Date().toISOString(),
    }
    return { message: 'Workflow run retried successfully', data: newRun }
  }

  // GET /api/v2/workflows — catalog list (must not match /workflow-types or /workflow-runs)
  if (normalizedPath === '/api/v2/workflows' && method === 'GET') {
    return {
      message: 'Workflow catalog retrieved successfully',
      data: [
        {
          id: 'wf-type-1',
          slug: 'yield-excursion',
          name: 'Yield Excursion',
          description: 'Detects yield excursions',
          category: 'SEMI_WORKFLOW',
          lifecycle_stage: 'ACTIVE',
          owner: null,
          is_emergence_built: true,
          current_version: 'v1.0.0',
          created_at: '2026-01-01T00:00:00Z',
          updated_at: '2026-01-02T00:00:00Z',
          stats: {
            total_runs: 12,
            avg_duration_seconds: null,
            last_run_at: null,
            last_run_by: null,
          },
        },
        {
          id: 'wf-type-2',
          slug: 'eval-harness',
          name: 'Evaluation Harness',
          description: 'Runs the evaluation pipeline for regression and scoring.',
          category: 'SEMI_WORKFLOW',
          lifecycle_stage: 'ACTIVE',
          owner: null,
          is_emergence_built: true,
          current_version: 'v1.0.0',
          created_at: '2026-01-01T00:00:00Z',
          updated_at: '2026-01-02T00:00:00Z',
          stats: { total_runs: 3, avg_duration_seconds: 90, last_run_at: null, last_run_by: null },
        },
        {
          id: 'wf-type-3',
          slug: 'feedback-learning',
          name: 'Feedback Learning',
          description: 'Applies pending feedback into policy updates.',
          category: 'SEMI_WORKFLOW',
          lifecycle_stage: 'ACTIVE',
          owner: null,
          is_emergence_built: true,
          current_version: 'v1.0.0',
          created_at: '2026-01-01T00:00:00Z',
          updated_at: '2026-01-02T00:00:00Z',
          stats: { total_runs: 0, avg_duration_seconds: null, last_run_at: null, last_run_by: null },
        },
        {
          id: 'wf-type-4',
          slug: 'ingestion-optimal-plus',
          name: 'Optimal+ Ingestion',
          description: 'Ingests Optimal+ exports into the warehouse.',
          category: 'SEMI_WORKFLOW',
          lifecycle_stage: 'ACTIVE',
          owner: null,
          is_emergence_built: true,
          current_version: 'v1.0.0',
          created_at: '2026-01-01T00:00:00Z',
          updated_at: '2026-01-02T00:00:00Z',
          stats: { total_runs: 44, avg_duration_seconds: 600, last_run_at: null, last_run_by: null },
        },
        {
          id: 'wf-type-5',
          slug: 'lot-trend-review',
          name: 'Lot Trend Review',
          description: 'Summarizes lot-level trends for engineering review.',
          category: 'SEMI_WORKFLOW',
          lifecycle_stage: 'DRAFT',
          owner: null,
          is_emergence_built: true,
          current_version: 'v0.9.0',
          created_at: '2026-01-01T00:00:00Z',
          updated_at: '2026-01-02T00:00:00Z',
          stats: { total_runs: 1, avg_duration_seconds: null, last_run_at: null, last_run_by: null },
        },
      ],
    }
  }

  const reservedWorkflowPathSlugs = new Set(['workflow-types', 'workflow-runs'])

  const workflowSlugVersionsMatch = normalizedPath.match(/^\/api\/v2\/workflows\/([^/]+)\/versions$/)
  if (workflowSlugVersionsMatch && method === 'GET') {
    const slug = workflowSlugVersionsMatch[1]
    if (!reservedWorkflowPathSlugs.has(slug)) {
      return {
        message: 'Workflow versions retrieved successfully',
        data: [
          {
            id: 'ver-1',
            version_label: 'v1.0.0',
            change_notes: 'Initial version',
            is_active: true,
            dag_json: {},
            created_at: '2026-01-01T00:00:00Z',
          },
        ],
      }
    }
  }

  const workflowSlugStatsMatch = normalizedPath.match(/^\/api\/v2\/workflows\/([^/]+)\/stats$/)
  if (workflowSlugStatsMatch && method === 'GET') {
    const slug = workflowSlugStatsMatch[1]
    if (!reservedWorkflowPathSlugs.has(slug)) {
      return {
        message: 'Workflow stats retrieved successfully',
        data: {
          usage: {
            total_runs: 10,
            unique_users: 2,
            last_run_date: '2026-01-02T00:00:00Z',
            manual_runs: 7,
            scheduled_runs: 3,
          },
          effectiveness: {
            success_rate: 0.8,
            failure_rate: 0.2,
            cancellation_rate: 0.0,
            total_feedback_count: 5,
            accepted_feedback_count: 3,
          },
          performance: {
            avg_runtime_seconds: 120.0,
            median_runtime_seconds: 100.0,
            p95_runtime_seconds: 180.0,
          },
          cost: { avg_tokens_per_run: null, avg_cost_per_run: null },
        },
      }
    }
  }

  const workflowSlugMetadataMatch = normalizedPath.match(/^\/api\/v2\/workflows\/([^/]+)$/)
  if (workflowSlugMetadataMatch && method === 'GET') {
    const slug = workflowSlugMetadataMatch[1]
    if (!reservedWorkflowPathSlugs.has(slug)) {
      return {
        message: 'Workflow metadata retrieved successfully',
        data: {
          id: 'wf-type-1',
          slug,
          name: slug === 'yield-excursion' ? 'Yield Excursion' : slug.replace(/-/g, ' '),
          description: 'Dev mock workflow metadata.',
          category: 'SEMI_WORKFLOW',
          lifecycle_stage: 'ACTIVE',
          owner: null,
          is_emergence_built: true,
          inputs_schema: { type: 'object', properties: {} },
          outputs_description: 'Mock outputs description.',
          processing_summary: 'Mock processing summary.',
          learning_description: null,
          current_version: {
            version_label: 'v1.0.0',
            dag_json: {},
            change_notes: null,
            created_at: '2026-01-01T00:00:00Z',
          },
          created_at: '2026-01-01T00:00:00Z',
          updated_at: '2026-01-02T00:00:00Z',
        },
      }
    }
  }

  // Handle GET /api/v2/workflows/workflow-types - list workflow types
  if (normalizedPath === '/api/v2/workflows/workflow-types' && method === 'GET') {
    return {
      message: 'Workflow types retrieved successfully',
      data: MOCK_WORKFLOW_TYPES_V2,
    }
  }

  // Handle GET /api/v2/workflows/workflow-types/{workflow_type} - get workflow type details
  const workflowTypeMatch = normalizedPath.match(/^\/api\/v2\/workflows\/workflow-types\/(.+)$/)
  if (workflowTypeMatch && method === 'GET') {
    const workflowType = decodeURIComponent(workflowTypeMatch[1])
    const meta = MOCK_WORKFLOW_TYPES_V2.find(
      (t) =>
        t.id === workflowType ||
        t.slug === workflowType ||
        (t.slug &&
          t.slug.trim().replace(/-/g, '_').toUpperCase() === workflowType),
    )
    return {
      message: 'Workflow type details retrieved successfully',
      data: {
        id: workflowType,
        name: meta?.name ?? workflowType,
        version: null,
        description: meta?.description ?? `Details for ${workflowType}`,
        parameter_schema: {
          type: 'object',
          properties: {
            product_id: { type: 'string' },
            lot_start_date: { type: 'string', format: 'date' },
            lot_end_date: { type: 'string', format: 'date' },
          },
          required: ['product_id', 'lot_start_date', 'lot_end_date'],
        },
      },
    }
  }

  // Handle GET /api/v2/workflows/workflow-runs/stats - get workflow stats
  if (normalizedPath === '/api/v2/workflows/workflow-runs/stats' && method === 'GET') {
    const items = mockWorkflowSessions.map(convertToWorkflowRunListItem)
    const byStatus: Record<string, number> = {}
    items.forEach((item) => {
      byStatus[item.status] = (byStatus[item.status] || 0) + 1
    })
    const byWorkflowType: Record<string, number> = {}
    items.forEach((item) => {
      const k = item.workflow_type
      byWorkflowType[k] = (byWorkflowType[k] || 0) + 1
    })
    const byTriggerType: Record<string, number> = {}
    items.forEach((item) => {
      const k = item.trigger_type
      byTriggerType[k] = (byTriggerType[k] || 0) + 1
    })

    return {
      message: 'Workflow stats retrieved successfully',
      data: {
        period: '30d',
        total_runs: items.length,
        by_status: byStatus,
        by_workflow_type: byWorkflowType,
        by_trigger_type: byTriggerType,
        avg_duration_seconds: 750,
        success_rate: 0.85,
      },
    }
  }

  // Chat is implemented via a2a proxy (/api/v2/chat/agent) and @a2a-js/sdk; no mock handlers here.

  // Handle GET /api/v2/products/{product_id}/lots - list lots for product (date range optional)
  const productLotsMatch = normalizedPath.match(/^\/api\/v2\/products\/([^/]+)\/lots(?:\?.*)?$/)
  if (productLotsMatch && method === 'GET') {
    const productId = productLotsMatch[1]
    const mockLots = [
      {
        id: 'LOT001',
        product_id: productId,
        name: 'LOT001',
        start_time: createISODate(10),
        end_time: createISODate(9),
        created_at: createISODate(11),
        number_of_wafers: 25,
        wafers_with_wat_data: 25,
        wafers_with_ws_data: 24,
        status: 'Complete',
        wafers: [],
      },
      {
        id: 'LOT002',
        product_id: productId,
        name: 'LOT002',
        start_time: createISODate(8),
        end_time: createISODate(7),
        created_at: createISODate(9),
        number_of_wafers: 25,
        wafers_with_wat_data: 24,
        wafers_with_ws_data: 25,
        status: 'Complete',
        wafers: [],
      },
      {
        id: 'LOT003',
        product_id: productId,
        name: 'LOT003',
        start_time: createISODate(5),
        end_time: createISODate(4),
        created_at: createISODate(6),
        number_of_wafers: 25,
        wafers_with_wat_data: 25,
        wafers_with_ws_data: 23,
        status: 'Partial',
        wafers: [],
      },
    ]
    return { message: 'Lots retrieved successfully', data: mockLots }
  }

  // Handle GET /api/v2/products/{product_id}/lots/metrics
  const productLotsMetricsMatch = normalizedPath.match(
    /^\/api\/v2\/products\/([^/]+)\/lots\/metrics(?:\?.*)?$/,
  )
  if (productLotsMetricsMatch && method === 'GET') {
    return {
      message: 'Metrics retrieved successfully',
      data: {
        lots_count: 3,
        total_wafers: 75,
        wafers_with_wat_data: 74,
        wafers_with_ws_data: 72,
        lots_with_wat_data: 3,
        lots_with_ws_data: 3,
        status: 'Complete',
      },
    }
  }

  // Handle data source by ID
  const dataSourceMatch = path.match(/^\/api\/v1\/data-sources\/(\d+)$/)
  if (dataSourceMatch && method === 'GET') {
    const dataSourceId = parseInt(dataSourceMatch[1], 10)
    const dataSource = mockDataSources.find((ds) => ds.id === dataSourceId)
    if (dataSource) {
      return { message: 'Data source retrieved successfully', data: dataSource }
    }
  }

  // Default: return from static map
  return mockDataMap[path]
}
